import { App } from '../App';
import { areClientEndpointsReadyToFetch } from 'utils/isLogged';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DEFAULT_TERM } from '../utils/constants';
import { getClientProductNumber } from '../utils/getProductNumber';
import { Response as GetClientResponse } from '../api/typegen/client/get';
import {
  getDefaultCalculatorFromSession,
  SESSION_CALCULATOR_TYPE,
} from '../utils/constantsSession';
import { getFetchHeaders } from '../utils/fetchHeaders';
import { Response } from '../api/typegen/application/constraints/get';
import { useConstraints } from './useConstraints';
import { useErrorManager } from '../InternalLibrary';
import useClient from './useClient';
import useClientOffer from './useClientOffer';

type Props = {
  children: ReactNode;
};

type FetchOptions = {
  pathParameters?: {
    productNumber: string;
  };
  queryParameters?: {
    amount: number | string;
    term: number | string;
  };
  client?: GetClientResponse;
};

type ClientConstraintsContextType = {
  data: Response | undefined | null;
  fetching: boolean;
  fetchClientConstraints: (
    options?: FetchOptions,
  ) => Promise<Response | undefined> | null | undefined;
};

const ClientConstraintsContext = createContext<ClientConstraintsContextType>(
  {} as ClientConstraintsContextType,
);

const useClientConstraints = (): ClientConstraintsContextType =>
  useContext(ClientConstraintsContext);

const ClientConstraintsProvider = ({ children }: Props): JSX.Element => {
  const [data, setData] = useState<Response | undefined | null>();
  const [fetching, setFetching] = useState(false);
  const { setError } = useErrorManager();
  const { client } = useClient();
  const { data: dataOffer } = useClientOffer();
  const { getConstraintsApi } = useConstraints();
  const searchParams = new URLSearchParams(window.location.search);

  const sessionCalculatorDataParsed: SESSION_CALCULATOR_TYPE | undefined =
    getDefaultCalculatorFromSession();

  const refFetching = useRef(fetching);

  const fetchClientConstraints = useCallback(
    async (options?: FetchOptions) => {
      try {
        const amountDefault =
          searchParams.get('amount') ||
          (sessionCalculatorDataParsed?.amount
            ? String(sessionCalculatorDataParsed?.amount)
            : undefined) ||
          dataOffer?.newPrincipal ||
          (options?.client || client)?.creditLimit ||
          App.config.fallbackAmount;

        const termDefault =
          searchParams.get('term') ||
          dataOffer?.term ||
          String(sessionCalculatorDataParsed?.term || DEFAULT_TERM);

        // parameters
        const amount = options?.queryParameters?.amount || amountDefault;
        const term = options?.queryParameters?.term || termDefault;

        setFetching(true);
        refFetching.current = true;

        const apiConstraints = getConstraintsApi({
          clientResponse: options?.client,
        });
        const nextData = await apiConstraints.fetch({
          headers: getFetchHeaders(),
          pathParameters: {
            productNumber:
              options?.pathParameters?.productNumber ||
              getClientProductNumber(client?.availableProducts),
          },
          queryParameters: {
            amount,
            term,
          },
        });

        setFetching(false);
        setData(nextData);
        return nextData;
      } catch (e) {
        setData(null);
        setError();
        throw e;
      } finally {
        setFetching(false);
        refFetching.current = false;
      }
    },
    [client, dataOffer, sessionCalculatorDataParsed],
  );

  useEffect(() => {
    const prefetch = async () => {
      const clientDataLoaded = areClientEndpointsReadyToFetch(client);
      if (data === undefined && clientDataLoaded) {
        try {
          await fetchClientConstraints();
        } catch {
          console.error('Error fetching client constraints');
        }
      }
    };
    prefetch();
  }, [client, data]);

  const clientContextValue = useMemo(
    () => ({
      data,
      fetchClientConstraints,
      fetching,
    }),
    [data, fetchClientConstraints, fetching],
  );

  return (
    <ClientConstraintsContext.Provider value={clientContextValue}>
      {children}
    </ClientConstraintsContext.Provider>
  );
};

export { useClientConstraints as default, ClientConstraintsProvider };
